import { upperFirst } from 'lodash-es'
import pluralize from 'pluralize'

import { FvButton, FvLinkButton, Icon } from '@fv/client-components'
import {
  formatNumber,
  getDistanceDisplay,
  totalShipmentWeight,
} from '@fv/client-core'
import type { PropsWithLoad } from '@fv/client-types'
import type { EquipmentType } from '@fv/models/equipment'

import { getDimensions } from '@/features/commodities/commodityUtils'
import { useLocale, useTypeNames } from '@/hooks/settings'
import { useLoadEditRoute } from '@/route-hooks'

import { ListItem } from '../List'

type SummaryProps = PropsWithLoad<{
  equipmentType?: EquipmentType
  isBooking?: boolean
  showItems?: () => void
}>

// TODO: Dims & class if multiple items?
export const LoadSummaryDetails = ({ load, showItems }: SummaryProps) => {
  const { distance, equipment, items, workflow } = load
  const { packageName } = useTypeNames()
  const locale = useLocale()

  if (workflow === 'truckload') return null

  const { description, weightUOM } = equipment
  const totalWeight = totalShipmentWeight(load)
  const dimensions = getDimensions(items[0])
  const freightClass = items[0]?.freightClass
  const handlingUnits = items
    .map(i => {
      const name = packageName(i.type)
      return pluralize(name, i.quantity, true)
    })
    .join(', ')

  return (
    <>
      <ListItem className="leading-5">
        <FvButton
          theme="underlined"
          icon="quote-left"
          iconClass="shrink-0"
          onClick={showItems}
          transform="down-2"
          className="flex !whitespace-normal text-left"
        >
          <p className="mb-0">
            {upperFirst(description) || 'No description given'}
          </p>
        </FvButton>
      </ListItem>

      {!!distance && (
        <ListItem>
          <Icon icon="route" className="text-fv-gray-dark" />
          <span>{getDistanceDisplay(load, locale)}</span>
        </ListItem>
      )}

      <ListItem itemsStart>
        <Icon
          icon="ramp-loading"
          className="text-fv-gray-dark shrink-0"
          transform="down-2.5"
        />
        <p className="mb-0">{handlingUnits}</p>
      </ListItem>

      <ListItem>
        <Icon icon="weight-hanging" className="text-fv-gray-dark" />
        <span>
          {formatNumber(totalWeight, locale)} {weightUOM}
        </span>
      </ListItem>

      {(!!dimensions || !!freightClass) && (
        <ListItem>
          <Icon icon="ruler" className="text-fv-gray-dark" />
          <span>
            {dimensions}
            {dimensions && freightClass ? ' / ' : ''}
            {freightClass ? `Class ${freightClass}` : ''}
          </span>
        </ListItem>
      )}
    </>
  )
}

export const LoadSummaryActions = ({
  load,
  className,
}: PropsWithLoad & { className?: string }) => {
  const { isFreightCollect, status, workflow } = load
  const editRoute = useLoadEditRoute()

  if (isFreightCollect || status !== 'pending' || workflow === 'truckload') {
    return null
  }

  return (
    <li className={className}>
      <FvLinkButton icon="pen-alt" to={editRoute(load, 'edit')}>
        <span>Change quote details</span>
      </FvLinkButton>
    </li>
  )
}
